import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { Button } from "@shared/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@shared/ui/card";
import { Input } from "@shared/ui/input";
import { Label } from "@shared/ui/label";
import { Checkbox } from "@shared/ui/checkbox";
import { Separator } from "@shared/ui/separator";
import { Textarea } from "@shared/ui/textarea";
import { Printer, FileText, CheckCircle } from "lucide-react";
import { Account } from "@features/cx/outstanding/types";
import { useState } from "react";
import { FormField, FormService } from "@shared/services/FormServices";
import { DynamicForm } from "@shared/components/DynamicForm";
import { useCreatePaymentAgreement } from "@features/cx/outstanding/hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { logEvent } from "@shared/analytics/analytics";

interface PaymentAgreementDialogProps {
  isOpen: boolean;
  onClose: () => void;
  account: any;
  monthlyBills: Array<{
    month: string;
    waterCharges: number;
    sewerCharges: number;
    lateFees: number;
    total: number;
    dueDate: string;
    status: string;
  }>;
  onAgreementCreated?: (agreementDetails: any) => void;
}

// Utility function to get ordinal suffix for day numbers
const getOrdinalSuffix = (day: number): string => {
  if (day > 3 && day < 21) return "th";
  switch (day % 10) {
    case 1: return "st";
    case 2: return "nd";
    case 3: return "rd";
    default: return "th";
  }
};

// Generate payment day options
const generatePaymentDayOptions = () => {
  return Array.from({ length: 28 }, (_, i) => {
    const day = i + 1;
    return {
      value: day.toString(),
      label: `${day}${getOrdinalSuffix(day)} of each month`
    };
  });
};

export function PaymentAgreementDialog({
  isOpen,
  onClose,
  account,
  monthlyBills,
  onAgreementCreated,
}: PaymentAgreementDialogProps) {
  console.log('account', account);
  const remoteUtilityId = useRemoteUtilityId();
  const remoteBillId = account?.billData?.id;
  const createPaymentAgreement = useCreatePaymentAgreement(Number(remoteUtilityId), Number(remoteBillId));
  const [downPayment, setDownPayment] = useState("");
  const [installments, setInstallments] = useState("3");
  const [waiveFee, setWaiveFee] = useState(false);
  const [paymentDayOfMonth, setPaymentDayOfMonth] = useState("1");
  const [notes, setNotes] = useState("");
  const [agreementCreated, setAgreementCreated] = useState(false);
  const [includeBilledAmount, setIncludeBilledAmount] = useState(false);

  const outstandingAmount = account?.billData?.outstandingBalance;
  const totalAmount = outstandingAmount;

  const arrangementFee = 25.0;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    }).format(date);
  };

  const generatePaymentSchedule = () => {
    const schedule = [];
    const today = new Date();
    for (let i = 1; i <= parseInt(installments); i++) {
      const dueDate = new Date(today);
      dueDate.setMonth(dueDate.getMonth() + i);
      schedule.push({
        installment: i,
        dueDate: dueDate.toISOString().split("T")[0],
        amount: monthlyPayment,
      });
    }
    return schedule;
  };

  const formFields: FormField[] = [
    {
      name: "downPayment",
      type: "decimal",
      label: "Down Payment",
      placeholder: "0.00",
      required: false,
      step: 0.01,
      className: "col-span-1 mt-3",
    },
    {
      name: "installments",
      type: "number",
      label: "Number of Installments",
      placeholder: "e.g., 3",
      required: false,
      className: "col-span-1 mt-3",
    },
    {
      name: "paymentDayOfMonth",
      type: "select",
      label: "Payment Day of Month",
      required: false,
      className: "col-span-2 mt-3",
      options: generatePaymentDayOptions()
    },
    {
      name: "notes",
      type: "textarea",
      label: "Notes (Optional)",
      placeholder: "Enter any additional notes for this payment agreement...",
      rows: 3,
      className: "col-span-2 mt-3",
    },
  ];

  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    validateOnMount: false,
    defaultValues: {
      downPayment,
      installments,
      paymentDayOfMonth,
      notes,
    },
  });

  const downPaymentAmount = parseFloat(form.getValues("downPayment")) || 0;
  const remainingAmount =
    totalAmount - downPaymentAmount;
  const monthlyPayment = remainingAmount / (parseInt(form.getValues("installments")));

  const handleSubmit = () => {
    const payload = {
      consumer: Number(account?.id),
      remoteBillId: Number(remoteBillId),
      remoteUtilityId: Number(remoteUtilityId),
      totalAgreementAmount: Number(totalAmount),
      downPayment: parseFloat(form.getValues("downPayment")),
      noOfInstallments: parseInt(form.getValues("installments")),
      remark: form.getValues("notes"),
      monthlyPayment: Number(monthlyPayment.toFixed(2)),
      paymentOfTheMonth: parseInt(form.getValues("paymentDayOfMonth")),
    };
    createPaymentAgreement.mutateAsync(payload, {
      onSuccess: () => {
        logEvent('Payment Agreement Created', {
          agreementAmount: Number(totalAmount),
          consumerId: Number(account?.id),
          termLength: parseInt(form.getValues("installments")),
        })
      }
    });
    form.reset();
    onClose()
  };

  const handlePrintAgreement = () => {
    const printWindow = window.open("", "_blank");
    if (printWindow) {
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Payment Agreement - ${account.accountNumber}</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 40px; line-height: 1.6; }
              .header { text-align: center; border-bottom: 2px solid #333; padding-bottom: 20px; margin-bottom: 30px; }
              .section { margin-bottom: 25px; }
              .table { width: 100%; border-collapse: collapse; margin: 15px 0; }
              .table th, .table td { border: 1px solid #ddd; padding: 8px; text-align: left; }
              .table th { background-color: #f5f5f5; }
              .signature-section { margin-top: 50px; }
              .signature-line { border-bottom: 1px solid #333; width: 300px; margin: 30px 0 10px 0; }
              @media print { body { margin: 20px; } }
            </style>
          </head>
          <body>
            <div class="header">
              <h1>PAYMENT AGREEMENT</h1>
              <p>Water & Sewer Services</p>
            </div>
            
            <div class="section">
              <h2>Account Information</h2>
              <p><strong>Account Number:</strong> ${account.accountNumber}</p>
              <p><strong>Customer Name:</strong> ${account.customerName}</p>
              <p><strong>Service Address:</strong> ${account.categoryDisplay}</p>
              <p><strong>Agreement Date:</strong> ${formatDate(
                new Date().toISOString()
              )}</p>
            </div>

            <div class="section">
              <h2>Outstanding Balance Breakdown</h2>
              <table class="table">
                <thead>
                  <tr>
                    <th>Billing Period</th>
                    <th>Water Charges</th>
                    <th>Sewer Charges</th>
                    <th>Late Fees</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  ${monthlyBills
                    .map(
                      (bill) => `
                    <tr>
                      <td>${bill.month}</td>
                      <td>${formatCurrency(bill.waterCharges)}</td>
                      <td>${formatCurrency(bill.sewerCharges)}</td>
                      <td>${formatCurrency(bill.lateFees)}</td>
                      <td>${formatCurrency(bill.total)}</td>
                    </tr>
                  `
                    )
                    .join("")}
                  <tr style="font-weight: bold; background-color: #f9f9f9;">
                    <td colspan="4">Total Outstanding</td>
                    <td>${formatCurrency(outstandingAmount)}</td>
                  </tr>
                  ${
                    includeBilledAmount
                      ? `
                  <tr style="font-weight: bold; background-color: #f9f9f9;">
                    <td colspan="4">Total Amount</td>
                    <td>${formatCurrency(totalAmount)}</td>
                  </tr>
                  `
                      : ""
                  }
                </tbody>
              </table>
            </div>

            <div class="section">
              <h2>Payment Agreement Terms</h2>
              <p><strong>Total Amount Due:</strong> ${formatCurrency(
                totalAmount
              )}</p>
              <p><strong>Down Payment:</strong> ${formatCurrency(
                downPaymentAmount
              )}</p>
              <p><strong>Arrangement Fee:</strong> ${
                waiveFee ? "WAIVED" : formatCurrency(arrangementFee)
              }</p>
              <p><strong>Remaining Balance:</strong> ${formatCurrency(
                remainingAmount
              )}</p>
              <p><strong>Number of Installments:</strong> ${installments}</p>
              <p><strong>Monthly Payment:</strong> ${formatCurrency(
                monthlyPayment
              )}</p>
              <p><strong>Payment Day:</strong> ${form.getValues("paymentDayOfMonth")}${getOrdinalSuffix(parseInt(form.getValues("paymentDayOfMonth")))} of each month</p>
            </div>

            <div class="section">
              <h2>Payment Schedule</h2>
              <table class="table">
                <thead>
                  <tr>
                    <th>Installment</th>
                    <th>Due Date</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  ${generatePaymentSchedule()
                    .map(
                      (payment) => `
                    <tr>
                      <td>${payment.installment}</td>
                      <td>${formatDate(payment.dueDate)}</td>
                      <td>${formatCurrency(payment.amount)}</td>
                    </tr>
                  `
                    )
                    .join("")}
                </tbody>
              </table>
            </div>

            ${
              notes
                ? `
            <div class="section">
              <h2>Additional Notes</h2>
              <p>${notes}</p>
            </div>
            `
                : ""
            }

            <div class="section">
              <h2>Terms and Conditions</h2>
              <ol>
                <li>The customer agrees to make payments according to the schedule above.</li>
                <li>Failure to make any scheduled payment may result in termination of this agreement.</li>
                <li>If this agreement is terminated, the full outstanding balance becomes immediately due.</li>
                <li>Current water and sewer charges must be paid when due during the agreement period.</li>
                <li>This agreement becomes effective upon signature by both parties.</li>
              </ol>
            </div>

            <div class="signature-section">
              <div style="display: flex; justify-content: space-between;">
                <div>
                  <div class="signature-line"></div>
                  <p>Customer Signature</p>
                  <p>Date: _________________</p>
                </div>
                <div>
                  <div class="signature-line"></div>
                  <p>Utility Representative</p>
                  <p>Date: _________________</p>
                </div>
              </div>
            </div>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  if (agreementCreated) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <CheckCircle className="mr-2 h-5 w-5 text-green-600" />
              Payment Agreement Created
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="text-center py-6">
              <CheckCircle className="mx-auto h-16 w-16 text-green-600 mb-4" />
              <h3 className="text-lg font-semibold mb-2">
                Agreement Successfully Created
              </h3>
              <p className="text-muted-foreground">
                Payment agreement has been generated for account{" "}
                {account.accountNumber}
              </p>
            </div>

            <Card>
              <CardContent className="p-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Total Amount:</span>
                    <p className="font-medium">{formatCurrency(totalAmount)}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Down Payment:</span>
                    <p className="font-medium">
                      {formatCurrency(downPaymentAmount)}
                    </p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">
                      Monthly Payment:
                    </span>
                    <p className="font-medium">
                      {formatCurrency(monthlyPayment)}
                    </p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Installments:</span>
                    <p className="font-medium">{installments} months</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="flex gap-2">
              <Button onClick={handlePrintAgreement} className="flex-1">
                <Printer className="mr-2 h-4 w-4" />
                Print Agreement
              </Button>
              <Button variant="outline" onClick={onClose} className="flex-1">
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Payment Agreement</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Account Summary</CardTitle>
              <CardDescription>
                Balance overview for {account.consumerNo}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span>Outstanding Amount:</span>
                  <span className="font-medium">
                    {formatCurrency(outstandingAmount)}
                  </span>
                </div>
                <Separator className="my-2" />
                <div className="flex justify-between font-bold">
                  <span>Total Agreement Amount:</span>
                  <span>{formatCurrency(totalAmount)}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="space-y-6">
            <DynamicForm
              fields={formFields}
              form={form}
              onSubmit={handleSubmit}
              onReset={onClose}
              gridColumns={2}
              responsiveColumns={{
                sm: 1,
                md: 2,
              }}
              config={{
                showResetButton: true,
                showSubmitButton: true,
                submitButtonText: "Create Agreement",
                resetButtonText: "Cancel",
                spacing: "comfortable",
                classes: {
                  form: "space-y-6",
                  button: "px-4 py-2 rounded-md font-medium transition-colors",
                  buttonContainer:
                    "flex justify-end gap-3 pt-6 border-gray-200 mt-8",
                  input:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
                  textarea:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical",
                  select:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white",
                  label: "block text-sm font-medium text-gray-700 mb-1",
                  error: "text-sm text-red-600 mt-1",
                  container: "space-y-1",
                },
              }}
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}